import dayjs, { Dayjs } from 'dayjs';
import { SCaseReducer } from './types';
import { INITIAL_STATE } from '../../constants';
import { SAddBatch, SAddedBatch, SGRNEntry, SBillPrint, SBatch } from '../../types';
import { GrnEntryUtils } from '../../utils';

const loadRows: SCaseReducer<{ rows: SGRNEntry[]; mode: 'new' | 'append' }> = (state, action) => {
  state.rows = action.payload.rows;
};

const handleRowChange: SCaseReducer<{
  key: string;
  value: unknown;
  rowIndex: number;
  batchIndex?: number;
}> = (state, action) => {
  const payload = action.payload;
  let row = state.rows[payload.rowIndex];
  switch (payload.key) {
    case 'qty': {
      row.poQty = +(payload.value as number);
      break;
    }
    case 'expiry': {
      const value = payload.value as Dayjs | null;
      row.batchDetails[payload.batchIndex || 0] = {
        ...row.batchDetails[payload.batchIndex || 0],
        expiry: dayjs(value).unix(),
      };
      break;
    }
    case 'rejectedQty': {
      const newValue = Math.min(
        +(payload.value as string | number),
        +row.batchDetails[payload.batchIndex || 0].receivedQty,
      );

      const updatedBatch = {
        ...row.batchDetails[payload.batchIndex || 0],
        rejectedQty: newValue,
        acceptedQty: +row.batchDetails[payload.batchIndex || 0].receivedQty - +newValue,
      };

      if (newValue === 0) {
        delete updatedBatch.rejectedReason;
      }

      row.batchDetails[payload.batchIndex || 0] = updatedBatch;
      break;
    }
    case 'receivedQty': {
      const remainingQty = GrnEntryUtils.getItemRemainingQty(
        row.poQty,
        row.batchDetails,
        payload.batchIndex || 0,
      );
      const newValue =
        +(payload.value as number | string) < remainingQty ? '' + payload.value : remainingQty;

      row.batchDetails[payload.batchIndex || 0] = {
        ...row.batchDetails[payload.batchIndex || 0],
        receivedQty: '' + newValue,
        acceptedQty: +newValue - +row.batchDetails[payload.batchIndex || 0].rejectedQty,
      };
      break;
    }
    case 'rejectedReason':
      if (payload.value === undefined) {
        const currentBatch = row.batchDetails[payload.batchIndex as number];

        delete currentBatch.rejectedReason;

        row.batchDetails[payload.batchIndex as number] = currentBatch;

        break;
      } else {
        const currentBatch = row.batchDetails[payload.batchIndex as number];

        currentBatch.rejectedReason = payload.value as string;

        row.batchDetails[payload.batchIndex as number] = currentBatch;

        break;
      }
    case 'mrp':
    case 'batchNo': {
      if (typeof payload.value === 'string') {
        row.batchDetails[payload.batchIndex || 0] = {
          ...row.batchDetails[payload.batchIndex || 0],
          [payload.key]: payload.value,
        };
        break;
      } else {
        const incomingBatch = payload.value as SBatch;
        row.batchDetails[payload.batchIndex || 0] = {
          ...row.batchDetails[payload.batchIndex || 0],
          id: row.id,
          batchNo: incomingBatch.batchNo as string,
          mrp: incomingBatch.mrp || 0,
          expiry: incomingBatch.expiry as number | null,
          stockId: incomingBatch.id,
        };
      }
      break;
    }
    default: {
      row = {
        ...row,
        [payload.key]: payload.value,
      };
      break;
    }
  }
  state.rows[payload.rowIndex] = row;
  state.client.totalGrn = GrnEntryUtils.calculateTotalGrn(state.rows); // Update Total Grn Counter
};

const resetState: SCaseReducer<undefined> = (state) => {
  state.rows = [];
  state.client.totalGrn = GrnEntryUtils.calculateTotalGrn(state.rows); // Update Total Grn Counter
};

const updateClient: SCaseReducer<{ key: string; value: unknown }> = (state, action) => {
  state.client = {
    ...state.client,
    [action.payload.key]: action.payload.value,
  };
};

const setPrintState: SCaseReducer<SBillPrint> = (state, action) => {
  state.client = {
    ...state.client,
    printState: {
      ...state.client.printState,
      ...action.payload,
    },
  };
};

const setAddBatchState: SCaseReducer<SAddBatch> = (state, action) => {
  state.client = {
    ...state.client,
    addBatchState: {
      ...state.client.printState,
      ...action.payload,
    },
  };
};

const handleExpandToggle: SCaseReducer<void> = (state) => {
  state.rows = state.rows.map((item) => ({
    ...item,
    openDetails: state.client.expandAll,
  }));
  state.client.expandAll = !state.client.expandAll;
};

const addBatchToItem: SCaseReducer<{
  rowIndex: number;
}> = (state, action) => {
  const payload = action.payload;
  const remainingQty =
    state.rows[payload.rowIndex].poQty -
    +state.rows[payload.rowIndex].batchDetails.reduce((acc, item) => acc + +item.receivedQty, 0);
  state.rows[payload.rowIndex].batchDetails.push({
    ...INITIAL_STATE.SBatchDetails(),
    receivedQty: '' + remainingQty,
    acceptedQty: remainingQty,
  });
  state.client.totalGrn = GrnEntryUtils.calculateTotalGrn(state.rows); // Update Total Grn Counter
};

const deleteBatchFromItem: SCaseReducer<{
  rowIndex: number;
  batchIndex: number;
}> = (state, action) => {
  const payload = action.payload;
  const batches = state.rows[payload.rowIndex].batchDetails.filter(
    (_, index) => index !== payload.batchIndex,
  );
  state.rows[payload.rowIndex].batchDetails = batches || [];
  state.client.totalGrn = GrnEntryUtils.calculateTotalGrn(state.rows); // Update Total Grn Counter
};

const loadBatchInBatchList: SCaseReducer<{ addedBatch: SAddedBatch }> = (state, action) => {
  const { addedBatch } = action.payload;
  const { rowIndex, batchIndex } = state.client.addBatchState;

  if (rowIndex !== undefined && batchIndex !== undefined) {
    // Add the new batch to the batches array
    (state.rows[rowIndex as number].batches as SBatch[]).push(addedBatch as unknown as SBatch);

    // Update the specific batch details
    const batchDetails = state.rows[rowIndex as number].batchDetails[batchIndex as number];
    batchDetails.batchNo = addedBatch.batchNo;
    batchDetails.mrp = addedBatch.mrp;
    batchDetails.stockId = addedBatch.id;
    batchDetails.expiry = addedBatch.expiry as number;
    batchDetails.id = state.rows[rowIndex as number].id;
  }

  const draftName = GrnEntryUtils.fetchLocalDraftId();

  GrnEntryUtils.saveLocalDraftByName(draftName, { items: state.rows });
};

export default {
  loadRows,
  handleRowChange,
  resetState,
  addBatchToItem,
  updateClient,
  setPrintState,
  setAddBatchState,
  deleteBatchFromItem,
  handleExpandToggle,
  loadBatchInBatchList,
};
